import { useMutation, useQuery } from "@apollo/client";
import { compact, isArray, omit } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  AssessmentGroupDelivery,
  AssessmentGroupListFragmentFragment,
  AssessmentTemplateComplianceRequirement,
  AssessmentType,
  ComplianceProgramAppliesTo,
  ComplianceProgramCalibrationType,
  ComplianceProgramRecurrence,
  ComplianceProgramState,
  GetAssessmentGroupsListQuery,
  GetAssessmentGroupsListQueryVariables,
  GetComplianceProgramAdminQuery,
  GetComplianceProgramAdminQueryVariables,
  GetTopicTemplatesQuery,
  GetTopicTemplatesQueryVariables,
  Maybe,
  OrganizationTalentIndicatorFragment,
  SaveComplianceProgramMutation,
  SaveComplianceProgramMutationVariables,
  TalentIndicatorQuestionSettingsNode,
} from "types/graphql-schema";

import getAssessmentGroupsListQuery from "@apps/assessments/graphql/get-assessment-groups-list-query";
import createOrUpdateComplianceProgramMutation from "@apps/programs/graphql/create-or-update-compliance-program-mutation";
import getComplianceProgramAdminQuery from "@apps/programs/graphql/get-compliance-program-admin-query";
import getComplianceProgramsQuery from "@apps/programs/graphql/get-compliance-programs-query";
import getTemplatesQuery from "@apps/templates/graphql/get-templates-query";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
  successNotificationVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import { ComboboxGenericOption } from "@components/combobox/generic-combobox";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import Tabs from "@components/tabs/tabs";
import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";
import useUrlQueryParams from "@helpers/hooks/use-url-query-params";

import ComplianceProgramFormReminders from "./compliance-program-form-reminders";
import ProgramFormParticipants from "./form-tabs/program-form-participants";
import ProgramFormQuestions from "./form-tabs/program-form-questions";
import ProgramFormWorkflow from "./form-tabs/program-form-workflow";
import {
  convertDeliveryTypeToDeliveryOption,
  convertEvaluationPeriodTypeToPeriod,
  defaultAssessmentTemplateSettings,
  getEvaluationPeriodType,
} from "./helpers";
import {
  AssessmentTemplateExtraSettings,
  ComplianceProgramStep,
  ComplianceProgramType,
  ProgramEvaluationPeriodType,
  ProgramForm,
  ProgramFormTab,
} from "./types";

const complianceProgramUrl = "/programs";

const getProgramTypeFromUrlParam = (programType?: ComplianceProgramType) => {
  if (programType === ComplianceProgramType.ONE_ON_ONE) {
    return ComplianceProgramType.ONE_ON_ONE;
  }
  if (programType === ComplianceProgramType.ENGAGEMENT_SURVEY) {
    return ComplianceProgramType.ENGAGEMENT_SURVEY;
  }
  return ComplianceProgramType.REVIEW;
};

const getProgramStepsFromProgramType = (
  programType?: ComplianceProgramType,
) => {
  if (programType === ComplianceProgramType.ENGAGEMENT_SURVEY) {
    return [ComplianceProgramStep.ENGAGEMENT_SURVEY];
  }
  if (programType === ComplianceProgramType.ONE_ON_ONE) {
    return [ComplianceProgramStep.ONE_ON_ONE];
  }
  return [
    ComplianceProgramStep.SELF_MANAGER_REVIEW,
    ComplianceProgramStep.PEER_REVIEW,
  ];
};

const getTalentIndicatorConfigFromTemplate = (
  talentIndicators: OrganizationTalentIndicatorFragment[],
  templateQuestionSets: {
    edges: ReadonlyArray<
      Maybe<{
        node?: Maybe<{
          talentIndicatorQuestionSettings?: Maybe<{
            talentIndicators?: ReadonlyArray<
              Maybe<TalentIndicatorQuestionSettingsNode>
            >;
          }>;
        }>;
      }>
    >;
  },
  isDuplicate = false,
) => {
  return templateQuestionSets.edges.some(
    (edge) => edge?.node?.talentIndicatorQuestionSettings,
  )
    ? {
        ...omit(
          templateQuestionSets.edges.find(
            (edge) => edge?.node?.talentIndicatorQuestionSettings,
          )?.node?.talentIndicatorQuestionSettings,
          ["__typename"],
        ),
        talentIndicators: compact(
          templateQuestionSets.edges.find(
            (edge) => edge?.node?.talentIndicatorQuestionSettings,
          )?.node?.talentIndicatorQuestionSettings?.talentIndicators,
        ).map((indicator) => {
          return {
            ...omit(indicator, ["__typename"]),
            description: isDuplicate
              ? assertNonNull(
                  talentIndicators.find(
                    (orgIndicator) =>
                      orgIndicator.id === indicator.talentIndicatorId,
                  ),
                ).description
              : indicator.description,
          };
        }),
      }
    : { talentIndicators: [] };
};

const ComplianceProgramForm = () => {
  const link = useLink();
  const location = useLocation();
  const isAdmin = isAdminVar();
  const organization = currentOrganizationVar();
  const currentUser = currentUserVar();

  const { complianceProgramId: complianceProgramIdParam } = useParams<{
    complianceProgramId: string;
  }>();
  const complianceProgramId = parseInt(complianceProgramIdParam);
  const isNew = isNaN(complianceProgramId);
  const isDuplicate = !isNew && location.pathname.includes("/duplicate");
  const { type: programTypeParam } = useUrlQueryParams<{
    type?: string;
  }>({
    type: undefined,
  });

  const [selectedTab, setSelectedTab] = useState(ProgramFormTab.Workflow);
  const [complianceProgramSteps, setComplianceProgramSteps] = useState<
    ComplianceProgramStep[]
  >(getProgramStepsFromProgramType(programTypeParam as ComplianceProgramType));
  const [complianceProgramType, setComplianceProgramType] =
    useState<ComplianceProgramType>(
      getProgramTypeFromUrlParam(programTypeParam as ComplianceProgramType),
    );
  const [proposedComplianceProgram, setProposedComplianceProgram] =
    useState<ProgramForm>({
      appliesTo: isAdmin
        ? ComplianceProgramAppliesTo.Organization
        : ComplianceProgramAppliesTo.CreatorDirectReports,
      appliesToTeams: [],
      recurrence: ComplianceProgramRecurrence.None,
      evaluationPeriod: {
        type: ProgramEvaluationPeriodType.PAST_HALF_YEAR,
      },
      sharesWithSubject: true,
      calibration: ComplianceProgramCalibrationType.PostCalibration,
      assessmentTemplates: [
        defaultAssessmentTemplateSettings(AssessmentType.Performance),
        defaultAssessmentTemplateSettings(AssessmentType.Peer),
      ],
    });
  const [availableAssessmentGroups, setAvailableAssessmentGroups] = useState<
    AssessmentGroupListFragmentFragment[]
  >([]);
  const [availableTopicTemplates, setAvailableTopicTemplates] = useState<
    ComboboxGenericOption<number>[]
  >([]);
  const [appliesToTeams, setAppliesToTeams] = useState<
    { id: number; title: string }[]
  >([]);
  const [appliesToManagers, setAppliesToManagers] = useState<
    { id: number; name: string }[]
  >([]);
  const [appliesToUsers, setAppliesToUsers] = useState<
    { id: number; name: string }[]
  >([]);
  const [excludedUsers, setExcludedUsers] = useState<
    { id: number; name: string }[]
  >([]);
  const [canUpdateTemplates, setCanUpdateTemplates] = useState(isNew);

  const homeUrl = useMemo(
    () =>
      isNew || isDuplicate
        ? complianceProgramUrl
        : `${complianceProgramUrl}/${complianceProgramId}`,
    [complianceProgramId, isNew, isDuplicate],
  );

  const [
    createOrUpdateComplianceProgram,
    { loading: isSavingComplianceProgram },
  ] = useMutation<
    SaveComplianceProgramMutation,
    SaveComplianceProgramMutationVariables
  >(createOrUpdateComplianceProgramMutation);

  const { data: complianceProgramData, loading: isLoadingComplianceProgram } =
    useQuery<
      GetComplianceProgramAdminQuery,
      GetComplianceProgramAdminQueryVariables
    >(getComplianceProgramAdminQuery, {
      variables: {
        complianceProgramId: isNew ? -1 : complianceProgramId,
        organizationId: organization.id,
        complianceProgramExists: !isNew,
      },

      onCompleted: (response) => {
        if (isNew) {
          return;
        }

        if (!response.complianceProgram) {
          link.redirect(homeUrl);
        } else {
          const topicTemplates = assertEdgesNonNull(
            response.complianceProgram.requiredTopicTemplates,
          );
          const talentIndicators = assertEdgesNonNull(
            response.talentIndicators,
          );
          const assessmentTemplates = compact([
            response.complianceProgram.performanceAssessmentTemplate && {
              id: response.complianceProgram.performanceAssessmentTemplate.id,
              assessmentType: AssessmentType.Performance,
              anonymity:
                response.complianceProgram.performanceAssessmentTemplate
                  .anonymity,
              providers:
                response.complianceProgram.performanceAssessmentTemplate
                  .providers,
              delivery:
                response.complianceProgram.performanceAssessmentTemplate
                  .delivery,
              enableRoleBasedAssessments:
                response.complianceProgram.performanceAssessmentTemplate
                  .enableRoleBasedAssessments,
              enableGoalAssessments:
                response.complianceProgram.performanceAssessmentTemplate
                  .enableGoalAssessments,
              enableCoreValueAssessments:
                response.complianceProgram.performanceAssessmentTemplate
                  .enableCoreValueAssessments,
              roleBasedAssessmentsAssessNextRole:
                response.complianceProgram.performanceAssessmentTemplate
                  .roleBasedAssessmentsAssessNextRole,
              roleBasedAssessmentsSelfAssessNextRole:
                response.complianceProgram.performanceAssessmentTemplate
                  .roleBasedAssessmentsSelfAssessNextRole,
              competencyAssessmentType:
                response.complianceProgram.performanceAssessmentTemplate
                  .competencyAssessmentType,
              nextRoleCompetencyAssessmentType:
                response.complianceProgram.performanceAssessmentTemplate
                  .nextRoleCompetencyAssessmentType,
              roleBasedAssessmentScale:
                response.complianceProgram.performanceAssessmentTemplate
                  .roleBasedAssessmentScale,
              roleBasedAssessmentScaleLabels:
                response.complianceProgram.performanceAssessmentTemplate
                  .roleBasedAssessmentScaleLabels,
              roleBasedAssessmentScaleStartsAt:
                response.complianceProgram.performanceAssessmentTemplate
                  .roleBasedAssessmentScaleStartsAt,
              goalAssessmentScale:
                response.complianceProgram.performanceAssessmentTemplate
                  .goalAssessmentScale,
              goalAssessmentScaleLabels:
                response.complianceProgram.performanceAssessmentTemplate
                  .goalAssessmentScaleLabels,
              goalAssessmentScaleStartsAt:
                response.complianceProgram.performanceAssessmentTemplate
                  .goalAssessmentScaleStartsAt,
              goalAssessmentResponses:
                response.complianceProgram.performanceAssessmentTemplate
                  .goalAssessmentResponses,
              coreValueAssessmentScale:
                response.complianceProgram.performanceAssessmentTemplate
                  .coreValueAssessmentScale,
              coreValueAssessmentScaleLabels:
                response.complianceProgram.performanceAssessmentTemplate
                  .coreValueAssessmentScaleLabels,
              coreValueAssessmentScaleStartsAt:
                response.complianceProgram.performanceAssessmentTemplate
                  .coreValueAssessmentScaleStartsAt,
              coreValueAssessmentResponses:
                response.complianceProgram.performanceAssessmentTemplate
                  .coreValueAssessmentResponses,
              questionSetIds:
                response.complianceProgram.performanceAssessmentTemplate.questionSets.edges
                  .filter(
                    (edge) => !edge?.node?.talentIndicatorQuestionSettings,
                  )
                  .map((edge) => assertNonNull(edge?.node?.id)),
              talentIndicatorConfig: getTalentIndicatorConfigFromTemplate(
                talentIndicators,
                response.complianceProgram.performanceAssessmentTemplate
                  .questionSets,
                isDuplicate,
              ),
            },
            response.complianceProgram.managerAssessmentTemplate && {
              id: response.complianceProgram.managerAssessmentTemplate.id,
              assessmentType: AssessmentType.Manager,
              anonymity:
                response.complianceProgram.managerAssessmentTemplate.anonymity,
              providers:
                response.complianceProgram.managerAssessmentTemplate.providers,
              delivery:
                response.complianceProgram.managerAssessmentTemplate.delivery,
              questionSetIds:
                response.complianceProgram.managerAssessmentTemplate.questionSets.edges
                  .filter(
                    (edge) => !edge?.node?.talentIndicatorQuestionSettings,
                  )
                  .map((edge) => assertNonNull(edge?.node?.id)),
              talentIndicatorConfig: getTalentIndicatorConfigFromTemplate(
                talentIndicators,
                response.complianceProgram.managerAssessmentTemplate
                  .questionSets,
                isDuplicate,
              ),
            },
            response.complianceProgram.peerAssessmentTemplate && {
              id: response.complianceProgram.peerAssessmentTemplate.id,
              assessmentType: AssessmentType.Peer,
              anonymity:
                response.complianceProgram.peerAssessmentTemplate.anonymity,
              providers:
                response.complianceProgram.peerAssessmentTemplate.providers,
              delivery:
                response.complianceProgram.peerAssessmentTemplate.delivery,
              enableRoleBasedAssessments:
                response.complianceProgram.peerAssessmentTemplate
                  .enableRoleBasedAssessments,
              enableGoalAssessments:
                response.complianceProgram.peerAssessmentTemplate
                  .enableGoalAssessments,
              enableCoreValueAssessments:
                response.complianceProgram.peerAssessmentTemplate
                  .enableCoreValueAssessments,
              roleBasedAssessmentsAssessNextRole:
                response.complianceProgram.peerAssessmentTemplate
                  .roleBasedAssessmentsAssessNextRole,
              roleBasedAssessmentsSelfAssessNextRole:
                response.complianceProgram.peerAssessmentTemplate
                  .roleBasedAssessmentsSelfAssessNextRole,
              competencyAssessmentType:
                response.complianceProgram.peerAssessmentTemplate
                  .competencyAssessmentType,
              nextRoleCompetencyAssessmentType:
                response.complianceProgram.peerAssessmentTemplate
                  .nextRoleCompetencyAssessmentType,
              roleBasedAssessmentScale:
                response.complianceProgram.peerAssessmentTemplate
                  .roleBasedAssessmentScale,
              roleBasedAssessmentScaleLabels:
                response.complianceProgram.peerAssessmentTemplate
                  .roleBasedAssessmentScaleLabels,
              roleBasedAssessmentScaleStartsAt:
                response.complianceProgram.peerAssessmentTemplate
                  .roleBasedAssessmentScaleStartsAt,
              goalAssessmentScale:
                response.complianceProgram.peerAssessmentTemplate
                  .goalAssessmentScale,
              goalAssessmentScaleLabels:
                response.complianceProgram.peerAssessmentTemplate
                  .goalAssessmentScaleLabels,
              goalAssessmentScaleStartsAt:
                response.complianceProgram.peerAssessmentTemplate
                  .goalAssessmentScaleStartsAt,
              goalAssessmentResponses:
                response.complianceProgram.peerAssessmentTemplate
                  .goalAssessmentResponses,
              coreValueAssessmentScale:
                response.complianceProgram.peerAssessmentTemplate
                  .coreValueAssessmentScale,
              coreValueAssessmentScaleLabels:
                response.complianceProgram.peerAssessmentTemplate
                  .coreValueAssessmentScaleLabels,
              coreValueAssessmentScaleStartsAt:
                response.complianceProgram.peerAssessmentTemplate
                  .coreValueAssessmentScaleStartsAt,
              coreValueAssessmentResponses:
                response.complianceProgram.peerAssessmentTemplate
                  .coreValueAssessmentResponses,
              questionSetIds:
                response.complianceProgram.peerAssessmentTemplate.questionSets.edges
                  .filter(
                    (edge) => !edge?.node?.talentIndicatorQuestionSettings,
                  )
                  .map((edge) => assertNonNull(edge?.node?.id)),
            },
            response.complianceProgram.engagementSurveyTemplate && {
              id: response.complianceProgram.engagementSurveyTemplate.id,
              assessmentType: AssessmentType.EngagementSurvey,
              anonymity:
                response.complianceProgram.engagementSurveyTemplate.anonymity,
              providers:
                response.complianceProgram.engagementSurveyTemplate.providers,
              delivery:
                response.complianceProgram.engagementSurveyTemplate.delivery,
              questionSetIds:
                response.complianceProgram.engagementSurveyTemplate.questionSets.edges
                  .filter(
                    (edge) => !edge?.node?.talentIndicatorQuestionSettings,
                  )
                  .map((edge) => assertNonNull(edge?.node?.id)),
            },
          ]);
          setAppliesToTeams(
            assertEdgesNonNull(response.complianceProgram.appliesToTeams),
          );
          setAppliesToManagers(
            assertEdgesNonNull(response.complianceProgram.appliesToManagers),
          );
          setAppliesToUsers(
            assertEdgesNonNull(response.complianceProgram.appliesToUsers),
          );
          setExcludedUsers(
            assertEdgesNonNull(response.complianceProgram.excludedUsers),
          );

          setProposedComplianceProgram({
            ...proposedComplianceProgram,
            ...response.complianceProgram,
            sharesWithSubject: assessmentTemplates.some(
              (template) => template.delivery !== AssessmentGroupDelivery.None,
            ),
            evaluationPeriod: getEvaluationPeriodType(
              response.complianceProgram,
            ),
            state: isDuplicate
              ? ComplianceProgramState.Draft
              : response.complianceProgram.state,
            scheduledReminders: assertEdgesNonNull(
              response.complianceProgram.scheduledReminders,
            )
              .filter(({ sentDatetime }) => sentDatetime === null)
              .map((reminder) => omit(reminder, "__typename", "sentDatetime")),
            complianceProgramId: response.complianceProgram.id,
            topicTemplates: topicTemplates.map(({ id }) => ({ id })),
            assessmentTemplates: isDuplicate
              ? assessmentTemplates.map((template) => omit(template, "id"))
              : assessmentTemplates,
            appliesTo:
              isAdmin ||
              response.complianceProgram.appliesTo !==
                ComplianceProgramAppliesTo.Organization
                ? response.complianceProgram.appliesTo
                : ComplianceProgramAppliesTo.CreatorDirectReports,
            appliesToTeams: appliesToTeams.map(({ id }) => id),
            appliesToManagers: appliesToManagers.map(({ id }) => id),
            appliesToUsers: appliesToUsers.map(({ id }) => id),
            excludedUsers: excludedUsers.map(({ id }) => id),
            periodStartDate: response.complianceProgram.periodStartDate,
            periodEndDate: response.complianceProgram.periodEndDate,
          });

          const hasEngagementSurveyTemplate =
            !!response.complianceProgram.engagementSurveyTemplate;
          const hasReviewTemplates =
            !!response.complianceProgram.performanceAssessmentTemplate ||
            !!response.complianceProgram.managerAssessmentTemplate ||
            !!response.complianceProgram.peerAssessmentTemplate;
          const hasOneOnOneTemplate = topicTemplates.length > 0;

          setComplianceProgramSteps(
            hasEngagementSurveyTemplate
              ? getProgramStepsFromProgramType(
                  ComplianceProgramType.ENGAGEMENT_SURVEY,
                )
              : compact([
                  response.complianceProgram.performanceAssessmentTemplate &&
                    ComplianceProgramStep.SELF_MANAGER_REVIEW,
                  response.complianceProgram.managerAssessmentTemplate &&
                    ComplianceProgramStep.UPWARD_REVIEW,
                  response.complianceProgram.peerAssessmentTemplate &&
                    ComplianceProgramStep.PEER_REVIEW,
                  hasOneOnOneTemplate && ComplianceProgramStep.ONE_ON_ONE,
                ]),
          );
          setComplianceProgramType(
            hasEngagementSurveyTemplate
              ? ComplianceProgramType.ENGAGEMENT_SURVEY
              : !hasReviewTemplates && hasOneOnOneTemplate
                ? ComplianceProgramType.ONE_ON_ONE
                : ComplianceProgramType.REVIEW,
          );

          setCanUpdateTemplates(
            isDuplicate ||
              response.complianceProgram.state === ComplianceProgramState.Draft,
          );
        }
      },
    });

  const { loading: isLoadingAvailableAssessmentGroups } = useQuery<
    GetAssessmentGroupsListQuery,
    GetAssessmentGroupsListQueryVariables
  >(getAssessmentGroupsListQuery, {
    variables: {
      organizationId: organization.id,
      archived: canUpdateTemplates ? false : null,
    },
    onCompleted: (response) => {
      const groups = response.assessmentGroups
        ? assertEdgesNonNull(response.assessmentGroups)
        : [];
      setAvailableAssessmentGroups(groups);
    },
  });

  const { loading: isLoadingAvailableTopicTemplates } = useQuery<
    GetTopicTemplatesQuery,
    GetTopicTemplatesQueryVariables
  >(getTemplatesQuery, {
    onCompleted: (response) => {
      const templates = response.topicTemplates
        ? assertEdgesNonNull(response.topicTemplates)
        : [];
      setAvailableTopicTemplates(
        templates.map(({ id, title }) => ({ value: id, label: title })),
      );
    },
  });

  const handleSaveProgram = useCallback(
    (state: ComplianceProgramState) => {
      createOrUpdateComplianceProgram({
        variables: {
          ...omit(
            proposedComplianceProgram,
            "evaluationPeriod",
            "sharesWithSubject",
          ),
          ...convertEvaluationPeriodTypeToPeriod(
            assertNonNull(proposedComplianceProgram.startDate),
            proposedComplianceProgram.evaluationPeriod,
          ),
          topicTemplates: canUpdateTemplates
            ? proposedComplianceProgram.topicTemplates
            : undefined,
          assessmentTemplates:
            canUpdateTemplates &&
            proposedComplianceProgram.assessmentTemplates &&
            isArray(proposedComplianceProgram.assessmentTemplates)
              ? convertDeliveryTypeToDeliveryOption(
                  proposedComplianceProgram.assessmentTemplates,
                  proposedComplianceProgram.sharesWithSubject,
                  proposedComplianceProgram.calibration ??
                    ComplianceProgramCalibrationType.Off,
                )
              : undefined,
          complianceProgramId:
            isNew || isDuplicate
              ? undefined
              : proposedComplianceProgram.complianceProgramId,
          organizationId: organization.id,
          state: state,
          appliesToTeams: appliesToTeams.map(({ id }) => id),
          appliesToManagers: appliesToManagers.map(({ id }) => id),
          appliesToUsers: appliesToUsers.map(({ id }) => id),
          excludedUsers: excludedUsers.map(({ id }) => id),
        },
        context: {
          errorMatches: [
            {
              match:
                "Program participants include employees not in your reporting tree",
              title:
                "Program participants include employees not in your reporting tree",
            },
            {
              match: "Can't update templates on a published program",
              title:
                "This program has already been published. Please refresh the page.",
            },
          ],
        },
        refetchQueries: [getComplianceProgramsQuery],
        onCompleted: () => {
          successNotificationVar({
            title:
              state === ComplianceProgramState.Draft
                ? "Program saved for later"
                : "Program published",
          });
          link.redirect(homeUrl);
        },
      });
    },
    [
      appliesToManagers,
      appliesToTeams,
      appliesToUsers,
      excludedUsers,
      createOrUpdateComplianceProgram,
      homeUrl,
      link,
      organization,
      proposedComplianceProgram,
      isNew,
      isDuplicate,
      canUpdateTemplates,
    ],
  );

  const handleUpdateProgramSteps = useCallback(
    (newSteps: ComplianceProgramStep[]) => {
      setComplianceProgramSteps(newSteps);
      const newTemplates = [
        ...(proposedComplianceProgram.assessmentTemplates as AssessmentTemplateComplianceRequirement[]),
      ];
      if (
        newSteps.includes(ComplianceProgramStep.SELF_MANAGER_REVIEW) &&
        !newTemplates.find(
          ({ assessmentType }) => assessmentType === AssessmentType.Performance,
        )
      ) {
        newTemplates.push(
          defaultAssessmentTemplateSettings(AssessmentType.Performance),
        );
      }
      if (
        newSteps.includes(ComplianceProgramStep.PEER_REVIEW) &&
        !newTemplates.find(
          ({ assessmentType }) => assessmentType === AssessmentType.Peer,
        )
      ) {
        newTemplates.push(
          defaultAssessmentTemplateSettings(AssessmentType.Peer),
        );
      }
      if (
        newSteps.includes(ComplianceProgramStep.UPWARD_REVIEW) &&
        !newTemplates.find(
          ({ assessmentType }) => assessmentType === AssessmentType.Manager,
        )
      ) {
        newTemplates.push(
          defaultAssessmentTemplateSettings(AssessmentType.Manager),
        );
      }
      if (
        newSteps.includes(ComplianceProgramStep.ENGAGEMENT_SURVEY) &&
        !newTemplates.find(
          ({ assessmentType }) =>
            assessmentType === AssessmentType.EngagementSurvey,
        )
      ) {
        newTemplates.push(
          defaultAssessmentTemplateSettings(AssessmentType.EngagementSurvey),
        );
      }

      setProposedComplianceProgram((prev) => ({
        ...prev,
        assessmentTemplates: newTemplates.filter(({ assessmentType }) => {
          return (
            (assessmentType === AssessmentType.Performance &&
              newSteps.includes(ComplianceProgramStep.SELF_MANAGER_REVIEW)) ||
            (assessmentType === AssessmentType.Peer &&
              newSteps.includes(ComplianceProgramStep.PEER_REVIEW)) ||
            (assessmentType === AssessmentType.Manager &&
              newSteps.includes(ComplianceProgramStep.UPWARD_REVIEW)) ||
            (assessmentType === AssessmentType.EngagementSurvey &&
              newSteps.includes(ComplianceProgramStep.ENGAGEMENT_SURVEY))
          );
        }),
      }));
    },
    [proposedComplianceProgram],
  );

  const handleChangeAssessmentTemplate = useCallback(
    (assessmentType: AssessmentType) =>
      (opt: ComboboxGenericOption<number> | null) => {
        const existingTemplate = proposedComplianceProgram.assessmentTemplates
          ? (
              proposedComplianceProgram.assessmentTemplates as AssessmentTemplateComplianceRequirement[]
            ).find((template) => template.assessmentType === assessmentType)
          : undefined;

        const defaultSettings =
          defaultAssessmentTemplateSettings(assessmentType);
        const newAssessmentTemplates = opt
          ? existingTemplate
            ? (
                proposedComplianceProgram.assessmentTemplates as AssessmentTemplateComplianceRequirement[]
              ).map((template) => {
                if (template.assessmentType === assessmentType) {
                  return {
                    ...defaultSettings,
                    ...template,
                    questionSetIds: [opt.value],
                  };
                }
                return template;
              })
            : [
                ...((proposedComplianceProgram.assessmentTemplates as AssessmentTemplateComplianceRequirement[]) ??
                  []),
                {
                  ...defaultSettings,
                  questionSetIds: [opt.value],
                },
              ]
          : (
              proposedComplianceProgram.assessmentTemplates as AssessmentTemplateComplianceRequirement[]
            ).map((template) => {
              if (template.assessmentType === assessmentType) {
                return {
                  ...defaultSettings,
                  ...template,
                  questionSetIds: [],
                };
              }
              return template;
            });
        setProposedComplianceProgram({
          ...proposedComplianceProgram,
          assessmentTemplates: compact(newAssessmentTemplates),
        });
      },
    [proposedComplianceProgram],
  );

  const handleChangeAssessmentTemplateSettings = useCallback(
    (assessmentType: AssessmentType) =>
      (settings: AssessmentTemplateExtraSettings) => {
        setProposedComplianceProgram((previousProposedProgram) => {
          const existingTemplate = previousProposedProgram.assessmentTemplates
            ? (
                previousProposedProgram.assessmentTemplates as AssessmentTemplateComplianceRequirement[]
              ).find((template) => template.assessmentType === assessmentType)
            : undefined;
          if (!existingTemplate) {
            return previousProposedProgram;
          }
          const newAssessmentTemplates = (
            previousProposedProgram.assessmentTemplates as AssessmentTemplateComplianceRequirement[]
          ).map((template) => {
            if (template.assessmentType === assessmentType) {
              const newTemplate = {
                ...template,
                ...settings,
              };

              // keep the scale and number of labels in sync
              if (newTemplate.enableRoleBasedAssessments) {
                if (
                  !newTemplate.roleBasedAssessmentScaleLabels ||
                  !newTemplate.roleBasedAssessmentScale
                ) {
                  throw new Error(
                    "Expecting Role based assessment scale and labels to be set",
                  );
                }
                if (
                  newTemplate.roleBasedAssessmentScaleLabels.length >
                  newTemplate.roleBasedAssessmentScale
                ) {
                  newTemplate.roleBasedAssessmentScaleLabels =
                    newTemplate.roleBasedAssessmentScaleLabels.slice(
                      0,
                      newTemplate.roleBasedAssessmentScale,
                    );
                } else if (
                  newTemplate.roleBasedAssessmentScaleLabels.length <
                  newTemplate.roleBasedAssessmentScale
                ) {
                  newTemplate.roleBasedAssessmentScaleLabels =
                    newTemplate.roleBasedAssessmentScaleLabels.concat(
                      Array(
                        newTemplate.roleBasedAssessmentScale -
                          newTemplate.roleBasedAssessmentScaleLabels.length,
                      ).fill(""),
                    );
                }
              }
              if (newTemplate.enableGoalAssessments) {
                if (
                  !newTemplate.goalAssessmentScaleLabels ||
                  !newTemplate.goalAssessmentScale
                ) {
                  throw new Error(
                    "Expecting Goal based assessment scale and labels to be set",
                  );
                }
                if (
                  newTemplate.goalAssessmentScaleLabels.length >
                  newTemplate.goalAssessmentScale
                ) {
                  newTemplate.goalAssessmentScaleLabels =
                    newTemplate.goalAssessmentScaleLabels.slice(
                      0,
                      newTemplate.goalAssessmentScale,
                    );
                } else if (
                  newTemplate.goalAssessmentScaleLabels.length <
                  newTemplate.goalAssessmentScale
                ) {
                  newTemplate.goalAssessmentScaleLabels =
                    newTemplate.goalAssessmentScaleLabels.concat(
                      Array(
                        newTemplate.goalAssessmentScale -
                          newTemplate.goalAssessmentScaleLabels.length,
                      ).fill(""),
                    );
                }
              }
              if (newTemplate.enableCoreValueAssessments) {
                if (
                  !newTemplate.coreValueAssessmentScaleLabels ||
                  !newTemplate.coreValueAssessmentScale
                ) {
                  throw new Error(
                    "Expecting Goal based assessment scale and labels to be set",
                  );
                }
                if (
                  newTemplate.coreValueAssessmentScaleLabels.length >
                  newTemplate.coreValueAssessmentScale
                ) {
                  newTemplate.coreValueAssessmentScaleLabels =
                    newTemplate.coreValueAssessmentScaleLabels.slice(
                      0,
                      newTemplate.coreValueAssessmentScale,
                    );
                } else if (
                  newTemplate.coreValueAssessmentScaleLabels.length <
                  newTemplate.coreValueAssessmentScale
                ) {
                  newTemplate.coreValueAssessmentScaleLabels =
                    newTemplate.coreValueAssessmentScaleLabels.concat(
                      Array(
                        newTemplate.coreValueAssessmentScale -
                          newTemplate.coreValueAssessmentScaleLabels.length,
                      ).fill(""),
                    );
                }
              }

              return newTemplate;
            }
            return template;
          });
          return {
            ...previousProposedProgram,
            assessmentTemplates: newAssessmentTemplates,
          };
        });
      },
    [],
  );

  const handleChangeComplianceProgramType = useCallback(
    (type: ComplianceProgramType) => {
      setComplianceProgramType(type);
    },
    [],
  );

  useEffect(() => {
    if (complianceProgramType === ComplianceProgramType.ENGAGEMENT_SURVEY) {
      handleUpdateProgramSteps([ComplianceProgramStep.ENGAGEMENT_SURVEY]);
    } else if (complianceProgramType === ComplianceProgramType.ONE_ON_ONE) {
      handleUpdateProgramSteps([ComplianceProgramStep.ONE_ON_ONE]);
    } else {
      handleUpdateProgramSteps([
        ComplianceProgramStep.SELF_MANAGER_REVIEW,
        ComplianceProgramStep.PEER_REVIEW,
      ]);
    }
    setProposedComplianceProgram((prev) => ({
      ...prev,
      topicTemplates: [],
    }));
    // update program steps when type changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complianceProgramType]);

  const handleAssessmentGroupCreated = useCallback(
    (assessmentGroup: AssessmentGroupListFragmentFragment) => {
      setAvailableAssessmentGroups((prev) => [
        ...prev.filter(({ id }) => id !== assessmentGroup.id),
        assessmentGroup,
      ]);
    },
    [],
  );
  const handleAssessmentGroupArchived = useCallback(
    (assessmentType: AssessmentType, assessmentGroupId: number) => {
      setAvailableAssessmentGroups((prev) =>
        prev.filter(({ id }) => id !== assessmentGroupId),
      );
      handleChangeAssessmentTemplate(assessmentType)(null);
    },
    [handleChangeAssessmentTemplate],
  );

  const isLoading =
    isLoadingComplianceProgram ||
    isLoadingAvailableAssessmentGroups ||
    isLoadingAvailableTopicTemplates;

  const numOfReviewSteps = complianceProgramSteps.filter((step) =>
    [
      ComplianceProgramStep.ENGAGEMENT_SURVEY,
      ComplianceProgramStep.PEER_REVIEW,
      ComplianceProgramStep.SELF_MANAGER_REVIEW,
      ComplianceProgramStep.UPWARD_REVIEW,
    ].includes(step),
  ).length;
  const canSaveTooltip =
    !proposedComplianceProgram.title ||
    proposedComplianceProgram.title.trim().length === 0
      ? "Please enter a title"
      : !proposedComplianceProgram.startDate &&
          !proposedComplianceProgram.startDateOffset
        ? "Please enter a start date"
        : !proposedComplianceProgram.dueDate &&
            !proposedComplianceProgram.startDateDueDateOffset
          ? "Please enter a due date"
          : complianceProgramSteps.length === 0
            ? "Please add at least one step"
            : numOfReviewSteps > 0 &&
                (!proposedComplianceProgram.assessmentTemplates ||
                  (
                    proposedComplianceProgram.assessmentTemplates as AssessmentTemplateComplianceRequirement[]
                  ).length !== numOfReviewSteps)
              ? "Each step requires a question set"
              : proposedComplianceProgram.assessmentTemplates &&
                  (
                    proposedComplianceProgram.assessmentTemplates as AssessmentTemplateComplianceRequirement[]
                  ).some((template) => template.questionSetIds.length === 0)
                ? "Each step requires a question set"
                : moment(proposedComplianceProgram.startDate).isAfter(
                      proposedComplianceProgram.dueDate,
                    )
                  ? "Start date must be before due date"
                  : null;

  return (
    <Layout>
      <Layout.Header
        breadcrumbs={[
          { title: "Programs", url: "/programs" },
          {
            title: `${
              isNew ? "New" : isDuplicate ? "Duplicate" : "Edit"
            } Program`,
            url: location.pathname,
          },
        ]}
      >
        {!isLoading && (
          <div className="flex justify-end items-center gap-2 sm:gap-4">
            <Button
              to={homeUrl}
              disabled={isSavingComplianceProgram}
              text="Discard changes"
            />
            {proposedComplianceProgram.state !==
              ComplianceProgramState.Published && (
              <Button
                type="button"
                onClick={() => handleSaveProgram(ComplianceProgramState.Draft)}
                tooltip={canSaveTooltip}
                disabled={!!canSaveTooltip || isSavingComplianceProgram}
                theme={buttonTheme.lightBlue}
                text={`${
                  isSavingComplianceProgram ? "Saving" : "Save"
                } for later`}
              />
            )}
            <Button
              type="button"
              onClick={() =>
                handleSaveProgram(ComplianceProgramState.Published)
              }
              tooltip={canSaveTooltip}
              disabled={!!canSaveTooltip || isSavingComplianceProgram}
              theme={buttonTheme.primary}
              text={
                proposedComplianceProgram.state !==
                ComplianceProgramState.Published
                  ? "Save and publish"
                  : "Save changes"
              }
            />
          </div>
        )}
      </Layout.Header>
      <Layout.Container>
        <main className="absolute inset-0">
          <div className="@container/layout-main mx-auto">
            {isLoading ? (
              <div className="mt-20 flex items-center justify-center h-full w-full">
                <Loading>Loading program...</Loading>
              </div>
            ) : (
              <>
                <div className="z-30 px-8 pt-6 sticky top-0 bg-white">
                  <Tabs
                    options={[
                      {
                        value: ProgramFormTab.Workflow,
                        label: "Workflow",
                      },
                      {
                        value: ProgramFormTab.Questions,
                        label: "Questions",
                      },
                      {
                        value: ProgramFormTab.Participants,
                        label: "Participants",
                      },
                      {
                        value: ProgramFormTab.Notifications,
                        label: "Notifications",
                      },
                    ]}
                    selectedValue={selectedTab}
                    onClickOptionValue={setSelectedTab}
                  />
                </div>
                <div className="absolute inset-0 overflow-y-scroll pt-20 pb-32">
                  <div className="max-w-screen-lg mx-auto">
                    {selectedTab === ProgramFormTab.Workflow && (
                      <ProgramFormWorkflow
                        complianceProgramType={complianceProgramType}
                        complianceProgramSteps={complianceProgramSteps}
                        canUpdateTemplates={canUpdateTemplates}
                        availableTopicTemplates={availableTopicTemplates}
                        onChangeComplianceProgramType={
                          handleChangeComplianceProgramType
                        }
                        proposedComplianceProgram={proposedComplianceProgram}
                        onUpdateProposedComplianceProgram={
                          setProposedComplianceProgram
                        }
                        canUpdate={canUpdateTemplates}
                        onUpdateComplianceProgramSteps={
                          handleUpdateProgramSteps
                        }
                        onUpdateAssessmentTemplateSettings={
                          handleChangeAssessmentTemplateSettings
                        }
                      />
                    )}
                    {selectedTab === ProgramFormTab.Questions && (
                      <ProgramFormQuestions
                        proposedComplianceProgram={proposedComplianceProgram}
                        onUpdateProposedComplianceProgram={
                          setProposedComplianceProgram
                        }
                        canUpdate={canUpdateTemplates}
                        complianceProgramSteps={complianceProgramSteps}
                        talentIndicatorData={
                          complianceProgramData
                            ? assertEdgesNonNull(
                                complianceProgramData?.talentIndicators,
                              )
                            : []
                        }
                        onUpdateComplianceProgramSteps={
                          handleUpdateProgramSteps
                        }
                        onUpdateAssessmentTemplateSettings={
                          handleChangeAssessmentTemplateSettings
                        }
                        onUpdateAssessmentTemplate={
                          handleChangeAssessmentTemplate
                        }
                        availableAssessmentGroups={availableAssessmentGroups}
                        onAssessmentGroupCreated={handleAssessmentGroupCreated}
                        onAssessmentGroupArchived={
                          handleAssessmentGroupArchived
                        }
                      />
                    )}
                    {selectedTab === ProgramFormTab.Participants && (
                      <ProgramFormParticipants
                        creator={
                          complianceProgramData?.complianceProgram?.creator ??
                          currentUser
                        }
                        proposedComplianceProgram={proposedComplianceProgram}
                        onUpdateProposedComplianceProgram={
                          setProposedComplianceProgram
                        }
                        canUpdate={canUpdateTemplates}
                        excludedUsers={excludedUsers}
                        onUpdateExcludedUsers={setExcludedUsers}
                        appliesToTeams={appliesToTeams}
                        onUpdateAppliesToTeams={setAppliesToTeams}
                        appliesToManagers={appliesToManagers}
                        onUpdateAppliesToManagers={setAppliesToManagers}
                        appliesToUsers={appliesToUsers}
                        onUpdateAppliesToUsers={setAppliesToUsers}
                      />
                    )}
                    {selectedTab === ProgramFormTab.Notifications && (
                      <ComplianceProgramFormReminders
                        proposedComplianceProgram={proposedComplianceProgram}
                        onUpdateProposedComplianceProgram={
                          setProposedComplianceProgram
                        }
                        canUpdate={canUpdateTemplates}
                        complianceProgramSteps={complianceProgramSteps}
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </Layout.Container>
    </Layout>
  );
};

export default ComplianceProgramForm;
